/* ==========================================================================
   ELIYEEN — product.js
   Product page behaviour: gallery (fade + swipe + thumbs + zoom),
   variant selection, price/stock/SKU updates, sticky mobile ATC, share.
   Depends on global.js (loaded first, both deferred).
   ========================================================================== */
(function () {
  'use strict';

  function init() {
    const page = document.querySelector('[data-product-page]');
    if (!page) return;

    const theme = window.theme || { strings: {}, settings: {} };
    const formatMoney = (window.eliyeen && window.eliyeen.formatMoney) || ((c) => '£' + (c / 100).toFixed(2));

    /* --- Data ------------------------------------------------------------- */
    let product = null;
    let inventory = {};
    try {
      product = JSON.parse(page.querySelector('[data-product-json]').textContent);
      inventory = JSON.parse(page.querySelector('[data-inventory-json]').textContent);
    } catch (err) {
      return; /* no data — static page still works */
    }

    /* Track for "recently viewed" */
    if (window.eliyeen && window.eliyeen.recentlyViewed) {
      window.eliyeen.recentlyViewed.push(product.handle);
    }

    /* --- Gallery ------------------------------------------------------------ */
    const gallery = page.querySelector('[data-gallery]');
    const slides = gallery ? Array.from(gallery.querySelectorAll('[data-gallery-slide]')) : [];
    const thumbs = gallery ? Array.from(gallery.querySelectorAll('[data-gallery-thumb]')) : [];
    const dots = gallery ? Array.from(gallery.querySelectorAll('.product-gallery__dot')) : [];
    let galleryIndex = 0;

    function showSlide(index) {
      if (!slides.length) return;
      galleryIndex = (index + slides.length) % slides.length;
      slides.forEach((s, i) => s.classList.toggle('is-active', i === galleryIndex));
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === galleryIndex));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === galleryIndex));
      const activeThumb = thumbs[galleryIndex];
      if (activeThumb) activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => showSlide(i)));

    const prevBtn = gallery && gallery.querySelector('[data-gallery-prev]');
    const nextBtn = gallery && gallery.querySelector('[data-gallery-next]');
    if (prevBtn) prevBtn.addEventListener('click', () => showSlide(galleryIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => showSlide(galleryIndex + 1));

    /* Swipe on touch */
    const mainStage = gallery && gallery.querySelector('[data-gallery-main]');
    if (mainStage) {
      let startX = null;
      let startY = null;
      mainStage.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      mainStage.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          showSlide(galleryIndex + (dx < 0 ? 1 : -1));
        }
        startX = null;
      }, { passive: true });
    }

    /* Zoom lightbox */
    const zoomModal = document.getElementById('ZoomModal');
    page.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-zoom-src]');
      if (!trigger || !zoomModal) return;
      const stage = zoomModal.querySelector('[data-zoom-stage]');
      stage.innerHTML = `<img src="${trigger.getAttribute('data-zoom-src')}" alt="" loading="eager">`;
      window.eliyeen.openModal(zoomModal);
    });

    /* --- Variant logic ----------------------------------------------------------- */
    const form = page.querySelector('form[data-product-form]');
    const idInput = form && form.querySelector('[data-variant-id-input]');
    const picker = page.querySelector('[data-variant-picker]');
    const priceWrapper = page.querySelector('[data-price-wrapper]');
    const atcButton = form && form.querySelector('[data-atc-button]');
    const atcText = form && form.querySelector('[data-atc-text]');
    const stickyBar = page.querySelector('[data-sticky-atc]');
    const stickyPrice = page.querySelector('[data-sticky-price]');
    const stickyBtn = page.querySelector('[data-sticky-atc-button]');
    const stickyText = page.querySelector('[data-sticky-atc-text]');
    const skuEl = page.querySelector('[data-sku]');
    const skuValue = page.querySelector('[data-sku-value]');
    const stockEl = page.querySelector('[data-stock-status]');
    const notifyEl = page.querySelector('[data-notify-me]');

    function selectedOptions() {
      if (!picker) return [];
      return Array.from(picker.querySelectorAll('.product-option')).map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    function findVariant() {
      const opts = selectedOptions();
      if (!opts.length) return product.variants[0];
      return product.variants.find((v) => opts.every((val, i) => v['option' + (i + 1)] === val));
    }

    function updatePrice(variant) {
      if (!priceWrapper || !variant) return;
      const priceEl = priceWrapper.querySelector('.price');
      if (!priceEl) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      priceEl.classList.toggle('price--on-sale', !!onSale);
      const current = priceEl.querySelector('.price__current');
      if (current) {
        /* keep the visually-hidden label, replace the amount text node */
        const hidden = current.querySelector('.visually-hidden');
        current.textContent = '';
        if (hidden) current.appendChild(hidden);
        current.appendChild(document.createTextNode(' ' + formatMoney(variant.price)));
      }
      let compare = priceEl.querySelector('.price__compare');
      if (onSale) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'price__compare';
          current.after(compare);
        }
        compare.textContent = formatMoney(variant.compare_at_price);
        compare.hidden = false;
      } else if (compare) {
        compare.hidden = true;
      }
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    }

    function updateButtons(variant) {
      const available = variant && variant.available;
      const label = !variant
        ? theme.strings.soldOut
        : available
          ? theme.strings.addToCart
          : theme.strings.soldOut;
      [
        [atcButton, atcText],
        [stickyBtn, stickyText]
      ].forEach(([btn, txt]) => {
        if (!btn) return;
        btn.toggleAttribute('disabled', !available);
        if (txt) txt.textContent = label;
      });
      if (notifyEl) notifyEl.toggleAttribute('hidden', !!available);
    }

    function updateStock(variant) {
      if (!stockEl) return;
      if (!variant) {
        stockEl.innerHTML = '';
        return;
      }
      const inv = inventory[String(variant.id)] || {};
      if (!variant.available) {
        stockEl.innerHTML = `<span class="stock-badge stock-badge--out">${theme.strings.outOfStock || theme.strings.soldOut}</span>`;
      } else if (inv.managed && inv.quantity > 0 && inv.quantity <= (parseInt(stockEl.getAttribute('data-low-threshold'), 10) || 5)) {
        stockEl.innerHTML = `<span class="stock-badge stock-badge--low">${(theme.strings.lowStock || 'Only [count] left in stock').replace('[count]', inv.quantity)}</span>`;
      } else {
        stockEl.innerHTML = `<span class="stock-badge stock-badge--in">${theme.strings.inStock || 'In stock'}</span>`;
      }
    }

    function updateSku(variant) {
      if (!skuEl || !skuValue) return;
      if (variant && variant.sku) {
        skuValue.textContent = variant.sku;
        skuEl.removeAttribute('hidden');
      } else {
        skuEl.setAttribute('hidden', '');
      }
    }

    function updateMedia(variant) {
      if (!variant || !variant.featured_media) return;
      const target = slides.findIndex((s) => s.getAttribute('data-media-id') === String(variant.featured_media.id));
      if (target >= 0) showSlide(target);
    }

    function updateUrl(variant) {
      if (!variant || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    function updateSelectedLabels() {
      if (!picker) return;
      picker.querySelectorAll('.product-option').forEach((fs) => {
        const checked = fs.querySelector('input:checked');
        const label = fs.querySelector('[data-option-selected]');
        if (checked && label) label.textContent = checked.value;
      });
    }

    function onVariantChange() {
      const variant = findVariant();
      if (variant && idInput) idInput.value = variant.id;
      updateSelectedLabels();
      updatePrice(variant);
      updateButtons(variant);
      updateStock(variant);
      updateSku(variant);
      updateMedia(variant);
      updateUrl(variant);
    }

    if (picker) picker.addEventListener('change', onVariantChange);

    /* --- Sticky mobile ATC bar -------------------------------------------------------- */
    if (stickyBar && form) {
      const buyBlock = form.querySelector('.product-details__buy') || form;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            stickyBar.classList.toggle('is-visible', show);
            stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
          });
        },
        { threshold: 0 }
      );
      io.observe(buyBlock);

      if (stickyBtn) {
        stickyBtn.addEventListener('click', () => {
          if (form.requestSubmit) form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        });
      }
    }

    /* --- Share ---------------------------------------------------------------------------- */
    const shareBtn = page.querySelector('[data-share-button]');
    if (shareBtn) {
      shareBtn.addEventListener('click', async () => {
        const data = { title: shareBtn.getAttribute('data-share-title') || document.title, url: window.location.href };
        if (navigator.share) {
          try { await navigator.share(data); } catch (err) { /* user dismissed */ }
        } else {
          try {
            await navigator.clipboard.writeText(data.url);
            const original = shareBtn.innerHTML;
            shareBtn.innerHTML = '✓ Link copied';
            setTimeout(() => { shareBtn.innerHTML = original; }, 1600);
          } catch (err) { /* clipboard unavailable */ }
        }
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
})();
